import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityComments } from '@/hooks/useEntityComments';
import { GenericSelect, RequiredMark, firstMissingRequiredMessage, type GenericFormField } from '@/styles/GenericForm';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StandardButton, SaveChangesButton, CancelButton, CloseButton, DeleteButton } from '@/styles/StandardButtons';
import { VendorField } from '@/components/shared/VendorField';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import { cn } from '@/lib/utils';
import { colors } from '@/design-system/colors';
import { BidItem, BidFormData, BidStatus } from '../types';
import { BID_CATEGORIES, BID_STATUS_OPTIONS } from '../constants';
import { bidApiService, type BidPayload } from '../services/bid-api-service';
import { bidAttachmentService } from '../services/bid-attachment-service';
import { bidPackageApi, type BidPackageRecord } from '../bid-packages/api';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { trackBidCreated, trackBidUpdated, trackFormSubmission } from '@/lib/posthog-events';

interface BidFormProps {
  onClose: () => void;
  onSubmit?: (data: BidFormData) => void;
  projectId?: string;
  bid?: any;
  bidItems?: BidItem[];
  attachments?: GenericAttachment[];
  projects?: { id: string; name: string }[];
  /** A comment or an attachment was added/removed without saving the bid -- the Overview's counters are stale. */
  onRelatedChange?: () => void;
  isOpen: boolean;
  /**
   * The bid belongs to a Bid Package (a vendor answering an invitation, or opening its own bid): the form shows only Vendor, Scope of work, Total, files and comments. The vendor is
   * the invitation's (it cannot be changed) and, when the package is not open, the bid is read-only.
   */
  bidPackage?: { id: string; title: string; project_id: string; vendor_id: string; vendor_name: string; status?: string };
}

type FormItem = Omit<BidItem, 'bid_id'>;

const createEmptyBidItem = (): FormItem => ({
  id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  description: '',
  unit: '',
  quantity: 0,
  unit_price: 0,
  total_price: 0,
});

const todayIso = () => new Date().toISOString().split('T')[0];

// Normalize function to ensure consistent types for the unsaved-changes comparison
const normalize = (obj: Record<string, any>): Record<string, string> =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v == null ? '' : String(v).trim()]));

const normalizeItems = (items: FormItem[]) =>
  items.map((item) => ({
    description: (item.description || '').trim(),
    unit: (item.unit || '').trim(),
    quantity: item.quantity ?? 0,
    unit_price: item.unit_price ?? 0,
    total_price: item.total_price ?? 0,
  }));

const inputStyles = 'font-form-input generic-form-field';

// stable defaults: a fresh `[]` per render would re-trigger the effects that depend on these props
const NO_ATTACHMENTS: GenericAttachment[] = [];
const NO_ITEMS: BidItem[] = [];
const NO_PROJECTS: { id: string; name: string }[] = [];

export function BidForm({
  onClose,
  onSubmit,
  projectId,
  bid,
  bidItems: initialBidItems = NO_ITEMS,
  attachments = NO_ATTACHMENTS,
  projects = NO_PROJECTS,
  onRelatedChange,
  isOpen,
  bidPackage,
}: BidFormProps) {
  const { companyId, user, userRole } = useUser();
  const { selectedProject } = useProject();
  // a vendor's bid in a package that is no longer open is read-only (the database repeats it)
  const packageReadOnly = !!bidPackage && !!bidPackage.status && bidPackage.status !== 'open' && userRole === 'vendor';
  // While creating a bid (not in package mode) the user may pick the package it answers -- optional for the company's own users, required for a vendor (the package is its only bridge to the company): only what the caller can read and still accepts bids.
  const vendorMode = userRole === 'vendor';
  const canPickPackage = isOpen && !bidPackage && !bid?.id;
  const [availablePackages, setAvailablePackages] = useState<BidPackageRecord[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [bidItems, setBidItems] = useState<FormItem[]>([]);
  const [otherCategory, setOtherCategory] = useState(bid?.other_category || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousIsOpenRef = useRef(false);
  const initialValuesRef = useRef<Record<string, any>>({});
  const initialOtherCategoryRef = useRef<string>('');
  const initialBidItemsRef = useRef<FormItem[]>([]);

  // Comments (generic architecture, entity_type='bid'): loaded and saved through the API, edit mode only.
  const { comments, addComment, deleteComment } = useEntityComments('bid', bid?.id);

  // --- Attachments: staged while the bid doesn't exist yet, uploaded right away once it does -------------------------
  const uploadFailuresRef = useRef<string[]>([]);
  const uploadBidAttachmentsFn = useCallback(async (bidId: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) {
      setError('Your session is not ready yet. Please try again in a moment.');
      return [];
    }
    const uploaded: GenericAttachment[] = [];
    for (const file of files) {
      try {
        uploaded.push(await bidAttachmentService.uploadBidAttachment(file, bidId, { companyId, userId: user.id }));
      } catch (uploadError) {
        const reason = uploadError instanceof Error ? uploadError.message : `"${file.name}" could not be uploaded`;
        uploadFailuresRef.current.push(reason);
        setError(reason);
      }
    }
    if (uploaded.length > 0) onRelatedChange?.();
    return uploaded;
  }, [companyId, user?.id, onRelatedChange]);

  const deleteBidAttachmentFn = useCallback(async (attachmentId: string): Promise<boolean> => {
    try {
      await bidAttachmentService.deleteBidAttachment(attachmentId, user?.id);
      onRelatedChange?.();
      return true;
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to delete the attachment');
      return false;
    }
  }, [user?.id, onRelatedChange]);

  const {
    pendingFiles,
    uploadedFiles: uploadedAttachments,
    setUploadedFiles: setUploadedAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteAttachment,
    uploadPending: uploadPendingFiles,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: bid?.id,
    uploadFn: uploadBidAttachmentsFn,
    deleteFn: deleteBidAttachmentFn,
    initialUploadedFiles: attachments,
    newFilesPosition: 'prepend',
    onError: setError,
  });

  useEffect(() => {
    setUploadedAttachments(attachments);
  }, [attachments, setUploadedAttachments]);

  // Capture the initial values ONLY when the form opens (closed -> open), never while it is open.
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      const initial = bid?.id
        ? {
            vendor_id: bid.vendor_id || '',
            vendor_name: bid.subcontractor_name || '',
            project_id: bid.project_id || '',
            bid_number: bid.bid_number || '',
            scope: bid.category || bid.scope || '',
            total_amount: bid.amount || bid.total_amount || 0,
            date_received: bid.date_received || todayIso(),
            valid_until: bid.valid_until || '',
            payment_terms: bid.payment_terms || '',
            description: bid.description || '',
            status: (bid.status as BidStatus) || 'received',
          }
        : {
            vendor_id: bidPackage?.vendor_id || '',
            vendor_name: bidPackage?.vendor_name || '',
            project_id: bidPackage?.project_id || projectId || getDefaultProjectIdForNewRecord(selectedProject),
            bid_number: '',
            scope: '',
            total_amount: 0,
            date_received: todayIso(),
            valid_until: '',
            payment_terms: '',
            description: '',
            status: 'received' as BidStatus,
          };

      initialValuesRef.current = normalize(initial);
      setFormData(initial);
      setError(null);
      initialOtherCategoryRef.current = (bid?.other_category || '').trim();
      setOtherCategory(bid?.other_category || '');

      const items: FormItem[] = bid?.id && initialBidItems.length > 0
        ? initialBidItems.map((item) => ({
            id: item.id,
            description: item.description || '',
            unit: item.unit || '',
            quantity: item.quantity ?? 0,
            unit_price: item.unit_price ?? 0,
            total_price: item.total_price ?? 0,
          }))
        : [createEmptyBidItem()];
      setBidItems(items);
      initialBidItemsRef.current = JSON.parse(JSON.stringify(items));
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bid, projectId, initialBidItems]);

  const handleChange = (name: string, value: any) => setFormData((prev) => ({ ...prev, [name]: value }));

  useEffect(() => {
    if (!canPickPackage) return undefined;
    let cancelled = false;
    setSelectedPackageId('');
    bidPackageApi.available(vendorMode).then((rows) => { if (!cancelled) setAvailablePackages(rows); }).catch(() => { if (!cancelled) setAvailablePackages([]); });
    return () => { cancelled = true; };
  }, [canPickPackage, vendorMode]);

  const selectedPackage = availablePackages.find((p) => p.id === selectedPackageId) || null;
  const packageOptions = useMemo(
    () => availablePackages
      .filter((p) => p.id === selectedPackageId || !formData.project_id || p.project_id === formData.project_id)
      .map((p) => ({ label: `${p.package_id ? `${p.package_id} · ` : ''}${p.title}${!formData.project_id && p.project?.name ? ` (${p.project.name})` : ''}`, value: p.id })),
    [availablePackages, selectedPackageId, formData.project_id],
  );
  // picking a package fills the project and locks it (the package decides it); clearing the pick leaves the project as it was
  const handlePackageChange = (id: string) => {
    setSelectedPackageId(id);
    const picked = availablePackages.find((p) => p.id === id);
    if (picked) handleChange('project_id', picked.project_id);
  };

  const packagePicker = canPickPackage ? (
    <div className="space-y-2">
      <Label htmlFor="bid_package_id" className="font-form-label">Bid Package<RequiredMark required={vendorMode} /></Label>
      <GenericSelect
        id="bid_package_id"
        value={selectedPackageId}
        onValueChange={handlePackageChange}
        placeholder="Select"
        required={vendorMode}
        searchable
        options={packageOptions}
      />
      {vendorMode && availablePackages.length === 0 && <p className="text-xs text-gray-500">You have no open bid packages to answer.</p>}
    </div>
  ) : null;

  const categoryOptions = useMemo(() => {
    const options = BID_CATEGORIES.map((category) => ({ label: category, value: category }));
    // a bid saved with an older category keeps showing it
    const current = formData.scope;
    return current && !(BID_CATEGORIES as readonly string[]).includes(current) ? [{ label: current, value: current }, ...options] : options;
  }, [formData.scope]);

  const isDirty = () => {
    const formDataChanged = JSON.stringify(normalize(formData)) !== JSON.stringify(initialValuesRef.current);
    const itemsChanged = JSON.stringify(normalizeItems(bidItems)) !== JSON.stringify(normalizeItems(initialBidItemsRef.current));
    const otherCategoryChanged = (otherCategory || '').trim() !== initialOtherCategoryRef.current;
    return formDataChanged || itemsChanged || otherCategoryChanged || pendingFiles.length > 0;
  };

  // Closing / reloading the browser tab with an open, modified form: the browser's own "leave site?" prompt.
  // (Closing the form itself asks in handleCloseAttempt; a SPA navigation cannot happen -- the overlay covers the app.)
  const dirtyNow = isOpen && isDirty();
  useEffect(() => {
    if (!dirtyNow) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyNow]);

  const handleCloseAttempt = () => {
    if (!isDirty() || window.confirm('Are you sure you want to lose all unsaved progress?')) onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleCloseAttempt();
  };

  const handleBidItemChange = (index: number, field: keyof BidItem, value: string | number) => {
    setBidItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price') next[index].total_price = Number(next[index].quantity) * Number(next[index].unit_price);
      return next;
    });
  };

  const removeBidItem = (index: number) => {
    if (bidItems.length > 1) setBidItems((prev) => prev.filter((_, i) => i !== index));
  };

  const requiredFields: GenericFormField[] = [
    { name: 'vendor_name', label: 'Vendor', type: 'text', required: true },
    { name: 'project_id', label: 'Project', type: 'select', required: true },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bidPackage) {
      // Bid Package mode: Scope of work and Total are required (the server and the database repeat it); the vendor, project and package come from the invitation
      if (!String(formData.description || '').trim()) { setError('Please fill in scope of work'); return; }
      if (!(Number(formData.total_amount) > 0)) { setError('Please fill in total bid amount'); return; }
      setIsSubmitting(true);
      setError(null);
      uploadFailuresRef.current = [];
      try {
        const body = { description: String(formData.description).trim(), amount: Number(formData.total_amount) };
        const saved = bid?.id
          ? (await bidApiService.updateBid(bid.id, body), { id: bid.id as string })
          : await bidApiService.createBid({ ...body, bid_package_id: bidPackage.id });
        await uploadPendingFiles(saved.id);
        if (uploadFailuresRef.current.length > 0) window.alert(`The bid was saved, but some files could not be attached:\n\n${uploadFailuresRef.current.join('\n')}`);
        onSubmit?.({ ...formData, total_amount: body.amount, bid_items: [] } as unknown as BidFormData);
        onClose();
      } catch (err: any) {
        setError(err.message || 'Failed to save bid');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    if (vendorMode) {
      // A vendor writes to the company only through a package it is assigned to: the package is required, and it decides the project and the vendor
      if (!selectedPackage) { setError('Please select a bid package'); return; }
      if (!String(formData.description || '').trim()) { setError('Please fill in scope of work'); return; }
      if (!(Number(formData.total_amount) > 0)) { setError('Please fill in total bid amount'); return; }
      setIsSubmitting(true);
      setError(null);
      uploadFailuresRef.current = [];
      try {
        const body = { description: String(formData.description).trim(), amount: Number(formData.total_amount) };
        const saved = await bidApiService.createBid({ ...body, bid_package_id: selectedPackage.id });
        await uploadPendingFiles(saved.id);
        if (uploadFailuresRef.current.length > 0) window.alert(`The bid was saved, but some files could not be attached:\n\n${uploadFailuresRef.current.join('\n')}`);
        onSubmit?.({ ...formData, total_amount: body.amount, bid_items: [] } as unknown as BidFormData);
        onClose();
      } catch (err: any) {
        setError(err.message || 'Failed to save bid');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    const missing = firstMissingRequiredMessage(requiredFields, { ...formData, project_id: projectId || formData.project_id });
    if (missing) {
      setError(missing);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    uploadFailuresRef.current = [];

    try {
      const selectedProjectId = projectId || formData.project_id;
      const vendorName = formData.vendor_name.trim();
      const totalAmount = bidItems.reduce((sum, item) => sum + item.total_price, 0);
      const finalTotalAmount = totalAmount > 0 ? totalAmount : formData.total_amount;
      if (selectedPackage) {
        if (!String(formData.description || '').trim()) { setError('A bid for a package needs the scope of work (Description)'); setIsSubmitting(false); return; }
        if (!(Number(finalTotalAmount) > 0)) { setError('A bid for a package needs the total amount'); setIsSubmitting(false); return; }
      }

      const payload: BidPayload = {
        project_id: selectedProjectId,
        vendor_id: formData.vendor_id || null,
        subcontractor_name: vendorName,
        bid_number: formData.bid_number || (bid?.id ? null : undefined),
        amount: finalTotalAmount,
        status: formData.status,
        date_received: formData.date_received,
        valid_until: formData.valid_until || (bid?.id ? null : undefined),
        payment_terms: formData.payment_terms,
        description: formData.description,
        category: formData.scope || null,
        other_category: formData.scope === 'Other' ? otherCategory : null,
        ...(!bid?.id && selectedPackage ? { bid_package_id: selectedPackage.id } : {}),
      };

      let savedBidId: string;
      if (bid?.id) {
        await bidApiService.updateBid(bid.id, payload);
        savedBidId = bid.id;
        trackBidUpdated({
          bid_id: bid.id,
          subcontractor_name: vendorName,
          category: formData.scope,
          amount: finalTotalAmount,
          status: formData.status,
          project_id: selectedProjectId,
          updated_by: 'user',
        });
      } else {
        // bid_number is the vendor's own quote number (free text, blank allowed); bid_id -- our internal
        // reference (e.g. "BD000001") -- is generated by the backend, never set here.
        const created = await bidApiService.createBid(payload);
        savedBidId = created.id;
        trackBidCreated({
          bid_id: created.id,
          subcontractor_name: vendorName,
          category: formData.scope,
          amount: finalTotalAmount,
          status: formData.status,
          project_id: selectedProjectId,
          date_received: formData.date_received,
          valid_until: formData.valid_until,
          payment_terms: formData.payment_terms,
          description: formData.description,
          company_id: companyId,
          created_by: user?.id,
        });
        trackFormSubmission({ form_type: 'bid_creation', success: true });
      }

      const itemsToPersist = bidItems
        .filter((item) => item.description.trim() || item.quantity > 0 || item.unit_price > 0)
        .map(({ description, unit, quantity, unit_price, total_price }) => ({ description, unit, quantity, unit_price, total_price }));
      if (bid?.id) await bidApiService.replaceBidItems(savedBidId, itemsToPersist);
      else await bidApiService.addBidItems(savedBidId, itemsToPersist);

      // Files picked before the bid existed. The bid is saved either way; say so if a file did not make it.
      await uploadPendingFiles(savedBidId);
      if (uploadFailuresRef.current.length > 0) {
        window.alert(`The bid was saved, but some files could not be attached:\n\n${uploadFailuresRef.current.join('\n')}\n\nOpen the bid to attach them again.`);
      }

      onSubmit?.({ ...formData, vendor_id: formData.vendor_id, total_amount: finalTotalAmount, bid_items: bidItems } as BidFormData);
      onClose();
    } catch (err: any) {
      console.error('Error saving bid:', err);
      setError(err.message || 'Failed to save bid');
      trackFormSubmission({
        form_type: bid?.id ? 'bid_update' : 'bid_creation',
        success: false,
        error_message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const itemInputStyles = 'h-7 text-xs border border-gray-200 rounded-md bg-white';
  const numberInputStyles = `${itemInputStyles} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

  const layout = (
    <div className="generic-form-overlay" style={{ top: 0, left: 0, right: 0, bottom: 0 }} onClick={handleOverlayClick}>
      <form
        onSubmit={handleSubmit}
        className="generic-form-container"
        style={{ width: 'var(--form-width-default)', maxHeight: 'var(--form-max-height-default)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cn('generic-form-header', 'generic-form-header-default')} style={{ backgroundColor: colors.formHeaderFooter }}>
          <h2 className="font-form-title">{bid?.id ? 'Edit Bid' : 'Create Bid'}{bidPackage ? ` · ${bidPackage.title}` : ''}</h2>
          <CloseButton onClick={handleCloseAttempt} />
        </div>

        <div className="generic-form-body generic-form-body-default">
          {error && <div className="generic-form-error">{error}</div>}

          <div className="space-y-6">
            {bidPackage && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="vendor_name" className="font-form-label">Vendor<RequiredMark required /></Label>
                  <Input id="vendor_name" value={formData.vendor_name || ''} readOnly disabled className={inputStyles} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="font-form-label">Scope of Work<RequiredMark required /></Label>
                  <Textarea id="description" value={formData.description || ''} disabled={packageReadOnly} onChange={(e) => handleChange('description', e.target.value)} placeholder="Describe the scope of work you are bidding" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_amount" className="font-form-label">Total Bid Amount ($)<RequiredMark required /></Label>
                  <Input
                    id="total_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.total_amount || ''}
                    disabled={packageReadOnly}
                    onChange={(e) => handleChange('total_amount', parseFloat(e.target.value) || 0)}
                    placeholder="$0.00"
                    className={`${inputStyles} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-gray-400`}
                    onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  />
                </div>
              </>
            )}
            {!bidPackage && vendorMode && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="vendor_name" className="font-form-label">Vendor</Label>
                  <Input id="vendor_name" value={formData.vendor_name || user?.email || ''} readOnly disabled className={inputStyles} />
                </div>
                {packagePicker}
                <div className="space-y-2">
                  <Label htmlFor="description" className="font-form-label">Scope of Work<RequiredMark required /></Label>
                  <Textarea id="description" value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} placeholder="Describe the scope of work you are bidding" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_amount" className="font-form-label">Total Bid Amount ($)<RequiredMark required /></Label>
                  <Input
                    id="total_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.total_amount || ''}
                    onChange={(e) => handleChange('total_amount', parseFloat(e.target.value) || 0)}
                    placeholder="$0.00"
                    className={`${inputStyles} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-gray-400`}
                    onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  />
                </div>
              </>
            )}
            {!bidPackage && !vendorMode && (
              <>
            {packagePicker}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="vendor_name" className="font-form-label">Vendor<RequiredMark required /></Label>
                <VendorField
                  id="vendor_name"
                  value={formData.vendor_name || ''}
                  onChange={({ id, name }) => setFormData((prev) => ({ ...prev, vendor_id: id || '', vendor_name: name }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project_id" className="font-form-label">Project<RequiredMark required /></Label>
                <GenericSelect
                  id="project_id"
                  value={formData.project_id || ''}
                  onValueChange={(value) => handleChange('project_id', value)}
                  required
                  disabled={!!selectedPackage}
                  placeholder="Select"
                  contentClassName="max-h-[18.75rem] overflow-y-auto"
                  options={projects.map((p) => ({ label: p.name, value: p.id }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="scope" className="font-form-label">Category</Label>
                <GenericSelect
                  id="scope"
                  value={formData.scope || ''}
                  onValueChange={(value) => {
                    handleChange('scope', value);
                    if (value !== 'Other') setOtherCategory('');
                  }}
                  placeholder="Select"
                  contentClassName="max-h-[18.75rem] overflow-y-auto"
                  options={categoryOptions}
                />
              </div>
              {formData.scope === 'Other' ? (
                <div className="space-y-2">
                  <Label htmlFor="other_category" className="font-form-label">Other Category</Label>
                  <Input id="other_category" value={otherCategory} onChange={(e) => setOtherCategory(e.target.value)} placeholder="Specify category" className={inputStyles} />
                </div>
              ) : (
                <div />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="bid_number" className="font-form-label">Bid Number</Label>
                <Input id="bid_number" value={formData.bid_number || ''} onChange={(e) => handleChange('bid_number', e.target.value)} placeholder="Vendor's quote number" className={inputStyles} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_received" className="font-form-label">Date Received</Label>
                <DateInput id="date_received" value={formData.date_received || ''} onChange={(iso) => handleChange('date_received', iso)} className={inputStyles} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid_until" className="font-form-label">Valid Until</Label>
                <DateInput id="valid_until" value={formData.valid_until || ''} onChange={(iso) => handleChange('valid_until', iso)} className={inputStyles} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="total_amount" className="font-form-label">Total Amount ($)</Label>
                <Input
                  id="total_amount"
                  type="number"
                  step="0.01"
                  value={formData.total_amount || ''}
                  onChange={(e) => handleChange('total_amount', parseFloat(e.target.value) || 0)}
                  placeholder="$0.00"
                  className={`${inputStyles} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-gray-400`}
                  onKeyDown={(e) => { if (['ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                  onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status" className="font-form-label">Status</Label>
                <GenericSelect id="status" value={formData.status || ''} onValueChange={(value) => handleChange('status', value)} placeholder="Select" options={BID_STATUS_OPTIONS} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="font-form-label">Description</Label>
              <Textarea id="description" value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} placeholder="Enter additional notes or description" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_terms" className="font-form-label">Payment Terms</Label>
              <Textarea id="payment_terms" value={formData.payment_terms || ''} onChange={(e) => handleChange('payment_terms', e.target.value)} placeholder="e.g., 30 days after completion" />
            </div>

            {/* Bid Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-bold">Bid Items</Label>
                <StandardButton type="button" onClick={() => setBidItems((prev) => [...prev, createEmptyBidItem()])} customVariant="white">
                  + Add Item
                </StandardButton>
              </div>
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <div className="grid grid-cols-6 gap-3 bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <span className="col-span-2">Description</span>
                  <span>Unit</span>
                  <span>Quantity</span>
                  <span>Unit Price ($)</span>
                  <span className="text-right">Total ($)</span>
                </div>
                <div className="divide-y">
                  {bidItems.map((item, index) => (
                    <div key={item.id} className="grid grid-cols-6 gap-3 items-center px-3 py-1.5 bg-white hover:bg-gray-50 transition-colors">
                      <div className="col-span-2">
                        <Input value={item.description} onChange={(e) => handleBidItemChange(index, 'description', e.target.value)} placeholder="Description" className={itemInputStyles} />
                      </div>
                      <div>
                        <Input value={item.unit} onChange={(e) => handleBidItemChange(index, 'unit', e.target.value)} placeholder="Unit" className={itemInputStyles} />
                      </div>
                      <div>
                        <Input type="number" value={item.quantity} onChange={(e) => handleBidItemChange(index, 'quantity', Number(e.target.value))} placeholder="0" className={numberInputStyles} />
                      </div>
                      <div>
                        <Input type="number" value={item.unit_price || ''} onChange={(e) => handleBidItemChange(index, 'unit_price', Number(e.target.value) || 0)} placeholder="$0.00" className={`${numberInputStyles} placeholder:text-gray-400`} />
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <Input value={item.total_price > 0 ? item.total_price.toFixed(2) : ''} readOnly placeholder="$0.00" className="bg-gray-50 text-right h-7 text-xs border border-gray-200 rounded-md placeholder:text-gray-400" />
                        {bidItems.length > 1 && <DeleteButton onClick={() => removeBidItem(index)} aria-label="Delete item" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
              </>
            )}

            <GenericAttachmentsSection
              attachments={uploadedAttachments}
              pendingFiles={pendingFiles}
              onUpload={handleFileUpload}
              onDelete={bid?.id ? handleDeleteAttachment : undefined}
              onRemovePending={handleRemovePendingFile}
              projectId={formData.project_id || ''} mode={bid?.id ? 'edit' : 'create'}
              disabled={packageReadOnly}
            />

            <GenericCommentsSection
              comments={comments}
              onAddComment={async (content, audience) => {
                await addComment(content, audience);
                onRelatedChange?.();
              }}
              onDeleteComment={async (commentId) => {
                if (!window.confirm('Are you sure you want to delete this comment?')) return;
                await deleteComment(commentId);
                onRelatedChange?.();
              }}
              mode={bid?.id ? 'edit' : 'create'}
              disabled={packageReadOnly}
              audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
              placeholder={bid?.id ? 'Add a comment...' : 'Comments are available after creating the bid'}
            />
          </div>
        </div>

        <div className={cn('generic-form-footer', 'generic-form-footer-default')} style={{ backgroundColor: colors.formHeaderFooter }}>
          <CancelButton onClick={handleCloseAttempt} />
          <SaveChangesButton disabled={isSubmitting || packageReadOnly}>{isSubmitting ? 'Saving...' : 'Save Changes'}</SaveChangesButton>
        </div>
      </form>
    </div>
  );

  return createPortal(layout, document.body);
}
